/**
 * "만든 이야기" - the five-act making-of, split into its own tab (user-directed,
 * 2026-08-15) so "어떻게 만들었나" stays pure structure (architecture + playground)
 * and the narrative gets room of its own.
 *
 * Two audiences at once: every act leads with plain language a middle-schooler can
 * follow (with a real-life analogy); engineering depth lives only in the muted
 * "🔬 기술 상세" paragraphs - renamed from "면접관용 상세" per user feedback (the old
 * label read as exclusionary). Nothing was cut in the rename.
 *
 * Act 5 frame: Triton was BENCHMARKED and its five safety guarantees rebuilt
 * in-process. The claim stays narrow: we did not reimplement Triton, we implemented
 * the guarantees a serving server exists for.
 *
 * "SF ML 엔진" (user-chosen, 2026-08-15) names that in-process inference layer. The word
 * "서버" is deliberately NOT in the name: act 5's whole point is that there is no separate
 * model server, so calling it one would contradict the page it lives on. The contrast
 * table's left column describes what plain model-loading does by default - it is not a
 * claim about any other team's system, and the intro says so.
 */

const JOURNEY: { id: string; label: string }[] = [
  { id: "act-problem", label: "🌉 해결하려는 문제" },
  { id: "act-fail", label: "📝 모델 불합격" },
  { id: "act-diagnosis", label: "🔬 한계 진단" },
  { id: "act-licence", label: "🪪 조건부 면허" },
  { id: "act-triton", label: "🚚 Triton 벤치마킹" },
];

export function Story() {
  function jump(id: string) {
    document.getElementById(id)?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }

  return (
    <>
      <p className="callout">
        이 탭은 <strong>이 시스템을 만든 순서 그대로, 5막짜리 이야기</strong>입니다.
        줄거리: ① 문제를 정하고 → ② 모델을 만들었지만 시험에서 떨어뜨리고 → ③ 왜
        떨어졌는지 계산해서 방향을 바꾸고 → ④ AI에게 조건부 면허를 발급하고 → ⑤ 유명한
        장비는 직접 벤치마킹해서 필요한 부분만 만들었다. 한 줄 요약:{" "}
        <strong>&quot;AI가 잘 맞힌다&quot; 자랑이 아니라, &quot;AI를 언제 믿고 언제 멈출
        것인가&quot;를 증명하는 이야기예요.</strong>
      </p>

      <nav className="journey-nav" aria-label="다섯 막 빠른 이동">
        {JOURNEY.map((stop) => (
          <button key={stop.id} type="button" onClick={() => jump(stop.id)}>
            {stop.label}
          </button>
        ))}
      </nav>

      <section className="card" id="act-problem">
        <h2>🌉 1막 — 해결하려는 문제: 두 개의 절벽 사이</h2>
        <p>
          은행 앞에는 절벽이 두 개 있어요. 검사를 안 하면 <strong>사기가 통과</strong>하고,
          전부 사람이 검사하면 <strong>은행이 멈춥니다.</strong> 그런데 진짜 무서운 건
          따로 있어요 — <strong>검사하는 AI가 고장 난 날.</strong> 고장 났다고 문을 활짝
          열어버리면 그날이 사기꾼의 축제날이죠.
        </p>
        <p className="callout">
          <strong>🔨 제1원칙:</strong> <strong>어떤 부품이 고장 나도, 절대 &quot;그냥
          통과&quot; 쪽으로 넘어지지 않는다.</strong> 모델이 죽어도, 확인이 안 돼도 →
          사람에게. 저장이 안 되면 → 성공한 척하지 않기. (기술 용어로 fail-safe)
        </p>
      </section>

      <section className="card" id="act-fail">
        <h2>📝 2막 — 모델을 만들었고, 시험에서 떨어뜨렸다</h2>
        <p>
          학습 모델(Logistic Regression)을 만들었어요. 단, 규칙이 하나 있습니다:{" "}
          <strong>시험 보기 전에 합격 기준을 먼저 종이에 적어둔다.</strong> 시험이 끝난 뒤
          기준을 바꾸면 그 시험은 무효니까요.
        </p>
        <p>
          <strong>🔨 결과: 불합격.</strong> 사기는 더 잘 찾았는데, 정작 목표였던 &quot;사람
          일 줄이기&quot;는 오히려 후퇴했어요. 그래서 투입하지 않았고,{" "}
          <strong>불합격 성적표를 지우지 않고 그대로 공개</strong>했습니다 (&quot;직접
          해보기&quot; 탭에서 지금도 보여요).
        </p>
        <p className="muted">
          🔬 기술 상세: 사전 등록 게이트(`application_gate_v1.json`, 결과 확인 전 고정),
          시간순 분할, 테스트셋 1회 접근, Shadow Mode(신규 모델을 관찰 전용으로 병행 실행)
          구현. 게이트 FAIL 판정 후 규칙 스코어러가 기본 서빙으로 유지됨.
        </p>
      </section>

      <section className="card" id="act-diagnosis">
        <h2>🔬 3막 — 왜 떨어졌는지 수학으로 진단하고, 방향을 바꿨다</h2>
        <p>
          &quot;모델을 더 키우면 되지 않을까?&quot;를 감이 아니라 <strong>계산으로</strong>{" "}
          확인했어요. 이 데이터에서 도달 가능한 최고 성능을 구해 보니, 지금 모델이 이미{" "}
          <strong>얻을 수 있는 신호의 96.9%</strong>를 쓰고 있었습니다. 문제는 모델이
          아니라 <strong>정보 부족</strong>이었어요.
        </p>
        <p>
          <strong>🔨 그래서 새 정보를 만들었습니다:</strong> 애매한 거래는 고객 폰으로
          &quot;본인이 보낸 것 맞나요?&quot;를 자동으로 물어봐요. 진짜 주인은 바로 누르고,
          폰이 없는 사기꾼은 못 누릅니다. 결과:{" "}
          <strong>사람 개입 82.14% → 55.54%</strong>, 놓친 사기 13건 → 6건.
        </p>
        <p className="muted">
          🔬 기술 상세: 오라클 상한 분석(`ml/CEILING.md`), 3밴드 정책(APPROVE / 자동
          본인확인 / REVIEW), 챌린지 실패·불가·미연결은 전부 REVIEW로. 통과율 가정은 비관·
          낙관 변형까지 사전 등록해 3가정 모두에서 반증 기준 통과(`ml/AUTOMATION.md`).
        </p>
      </section>

      <section className="card" id="act-licence">
        <h2>🪪 4막 — 사기범은 적응한다: 그래서 AI에 &quot;조건부 면허&quot;를 발급했다</h2>
        <p>
          <strong>🔨 AI에게 운전면허 제도를 그대로 적용했습니다.</strong> 합격한 조건에서만
          운전하고, 큰 위반이면 정지되고, 재발급은 사람만:
        </p>
        <ul className="plain">
          <li>
            <strong>면허 시험</strong>: 공격을 직접 시뮬레이션해 어디까지 버티는지 측정
            (본인확인 우회율 0.65까지 안전, 현재 가정 0.11)
          </li>
          <li>
            <strong>면허증</strong>: 그 안전 범위를 모델·데이터·정책의 지문(체크섬)과 함께
            기록 — 부품 하나만 바뀌어도 무효
          </li>
          <li>
            <strong>면허 정지</strong>: 수치가 허용선을 벗어나면 <strong>스스로 자동화를
            멈추고</strong> 전부 사람에게 (직접 해보기 탭의 🥊 공격 데모)
          </li>
          <li>
            <strong>재발급은 사람만</strong>: 공격이 잠잠해져도 혼자 다시 켜지지 않아요
          </li>
        </ul>
        <p className="callout">
          한 줄 요약: <strong>대부분의 포트폴리오는 &quot;AI가 얼마나 잘 맞히는지&quot;를
          보여줍니다. 이 프로젝트는 &quot;AI가 언제 멈춰야 하는지&quot;까지 구현합니다.</strong>
        </p>
        <p className="muted">
          🔬 기술 상세: 공격면을 런타임 관측가능성으로 분해 — 챌린지 우회는 원리적으로
          실시간 탐지 불가(통과한 챌린지는 라벨 없이 정상 통과와 구별 불가)라 오프라인
          headroom으로, 위장 침투는 관측 가능(자동승인 점유율 상승)이라 런타임 거버너로.
          두 방어층이 증명 가능하게 disjoint한 공격면을 커버(`ml/ADVERSARIAL.md`). 강등은
          sticky, 재승인은 토큰 인증된 사람만.
        </p>
      </section>

      <section className="card" id="act-triton">
        <h2>⚖️ 5막 — 업계 표준 장비를 벤치마킹해서, 필요한 부분만 직접 만들었다</h2>
        <p>
          편의점 갈 때는 트럭보다 자전거가 빨라요. AI 서빙의 트럭이 NVIDIA{" "}
          <strong>Triton</strong>(대형 모델용 업계 표준 서버)인데, &quot;유명하니까
          쓰자&quot;가 아니라 <strong>진짜로 설치해서 스톱워치로 쟀습니다.</strong>
        </p>
        <ul className="plain">
          <li>
            <strong>계산이 똑같은가?</strong> 18,000건 전부 비교 → 최대 오차 0.00000035.
            변환은 완벽했어요. ✅
          </li>
          <li>
            <strong>속도는?</strong> 지금 방식 0.13ms vs Triton 23.8ms —{" "}
            <strong>약 190배 느림.</strong> 이 시스템의 모델은 파일 크기 4KB인데, Triton은
            26GB짜리 트럭이거든요. ❌
          </li>
          <li>
            <strong>고장 나면?</strong> 서버를 일부러 꺼보니 판정이 전부 &quot;사람에게&quot;로
            안전하게 넘어감. ✅
          </li>
        </ul>
        <p className="callout">
          <strong>🔨 결론: 이 시스템에는 트럭을 안 산다.</strong> 대신 재기{" "}
          <em>전에</em> 합격 기준을 적어뒀고(2막과 같은 규칙), &quot;모델이 커지면 그때는
          산다&quot;는 조건도 문서로 남겼습니다.
        </p>
        <p>
          그런데 여기서 진짜 배운 게 있어요. 트럭을 뜯어보니{" "}
          <strong>전용 서버를 쓰는 진짜 이유는 속도가 아니라 &quot;안전장치&quot;</strong>
          더라고요. 그래서 트럭은 안 샀지만, <strong>트럭에 달려 있던 안전장치 5개를
          벤치마킹해서 우리 크기에 맞게 직접 만들어 달았습니다.</strong> 이렇게요:
        </p>
        <ul className="plain">
          <li>
            🔒 <strong>모델 파일이 바꿔치기됐는지 확인</strong> — 파일마다 지문이 있어서,
            지문이 다르면 아예 켜지지 않아요.
          </li>
          <li>
            🧪 <strong>매번 검산 문제를 풀어봄</strong> — 학습할 때 미리 정답을 적어둔 문제
            몇 개를 켜질 때마다 다시 풀어서, 답이 그때와 똑같은지 확인해요.
          </li>
          <li>
            📋 <strong>입력 칸이 학습 때와 같은지 확인</strong> — 항목 이름이나 순서가
            하나라도 다르면 채점을 거부합니다.
          </li>
          <li>
            🧑‍💼 <strong>뭐가 고장 나든 결과는 &quot;사람에게&quot;</strong> — 어떤
            실패도 자동 승인 쪽으로는 절대 넘어가지 않아요.
          </li>
          <li>
            🧾 <strong>누가 매긴 점수인지 기록</strong> — 판정마다 어떤 모델 버전이 채점했는지
            남깁니다.
          </li>
        </ul>
        <p>
          <strong>Triton을 흉내 내서 다시 만든 게 아니라, 트럭이 존재하는 이유였던 안전
          점검만 배워와 트럭 없이 같은 수준으로 단 것</strong>이에요. 도구를 쓸 줄 아는 것
          다음 단계는, <strong>그 도구가 왜 있는지 알고 내 크기에 맞게 다시 만드는
          것</strong>이라고 생각했습니다. 이렇게 완성된 추론 계층을 이 프로젝트에서는{" "}
          <strong>SF ML 엔진</strong>이라고 부릅니다.
        </p>

        <h3>⚖️ SF ML 엔진은 무엇이 다른가</h3>
        <p>
          아래 표의 왼쪽은 <strong>안전장치를 따로 넣지 않고 모델을 그대로 불러 쓸 때의 기본
          동작</strong>이에요. 누구를 깎아내리는 비교가 아니라, 같은 상황에서 이 엔진이 무엇을
          더 하는지를 나란히 놓은 것입니다.
        </p>
        <table className="evidence-table">
          <thead>
            <tr>
              <th>이런 일이 생기면</th>
              <th>안전장치를 넣지 않으면</th>
              <th>SF ML 엔진</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>모델 파일이 바뀌어 있음</td>
              <td>그대로 불러서 씀</td>
              <td>지문(체크섬) 불일치 → <strong>로드 거부</strong></td>
            </tr>
            <tr>
              <td>배포된 계산이 학습 때와 미묘하게 다름</td>
              <td>조용히 틀린 점수를 냄</td>
              <td>골든 샘플 재생(오차 1e-9) → <strong>채점 거부</strong></td>
            </tr>
            <tr>
              <td>입력 항목의 이름·순서가 바뀜</td>
              <td>에러 또는 엉뚱한 점수</td>
              <td>스키마·순서 검증 → <strong>채점 거부</strong></td>
            </tr>
            <tr>
              <td>추론이 실패함</td>
              <td>오류를 던지거나 기본값으로 통과</td>
              <td>모든 실패가 <strong>사람 검토로 수렴</strong> (자동 승인 불가)</td>
            </tr>
            <tr>
              <td>공격으로 안전 범위를 벗어남</td>
              <td>사람이 알아채고 끌 때까지 계속 돔</td>
              <td>인증서 + 거버너가 <strong>스스로 자동화를 정지</strong></td>
            </tr>
            <tr>
              <td>나중에 &quot;왜 그렇게 판정했나&quot;를 물음</td>
              <td>요청 로그를 뒤짐</td>
              <td>판정마다 <strong>모델 버전이 기록</strong>되어 있음</td>
            </tr>
          </tbody>
        </table>
        <p className="callout">
          앞의 네 줄은 잘 만든 팀이라면 하는 일이에요. <strong>이 프로젝트가 진짜로 다른
          지점은 다섯 번째 줄 — 모델이 스스로 자격을 잃는다는 것</strong>입니다. 실무 FDS
          에서도 드문 계층이고(&quot;한계와 다음 단계&quot; 탭의 위치 표), 그래서 이
          프로젝트의 기여로 적어뒀습니다.
        </p>
        <p className="muted">
          🔬 기술 상세 ①: 서빙용 JSON 산출물만 읽어 표준화+계수를 단일 Gemm+Sigmoid(행렬곱
          한 번 + 확률 변환 한 번)로 접은 ONNX export, Triton 24.08(onnxruntime backend,
          KServe v2 HTTP) 실서빙. 측정 전에 판정 기준을 등록했고, 전 테스트셋 결과일치
          관문과 장애 방향 유닛 테스트 5개를 통과시킨 뒤 비교. 원자료는{" "}
          <code>docs/adr/ADR-023</code>·<code>docs/perf/triton_poc.json</code>, ONNX 변환
          정합성은 매 커밋 CI에서 재검증. 교체 지점은 <code>Predictor</code> Protocol —
          재도입은 아키텍처 변경이 아니라 와이어링 변경이다.
        </p>
        <p className="muted">
          🔬 기술 상세 ②: 위 안전장치 5종은 별도 서빙 서버가 제공하는 것과 1:1로 대응하며
          인프로세스 <code>MLPredictor</code>에 구현됨 — ① 아티팩트 무결성 검증(SHA-256
          체크섬 불일치 시 로드 거부), ② 골든 샘플 스모크(학습 시 기록된 확률을 순수 Python
          경로로 재생, 허용 오차 1e-9), ③ 스키마·피처 순서 검증(불일치 시 채점 거부),
          ④ 모든 실패가 <code>PredictorUnavailableError</code> → REVIEW로 수렴(자동 승인
          절대 불가), ⑤ 판정별 <code>model_version</code> 감사 기록.
        </p>
      </section>

      <section className="card">
        <h2>🧪 데이터 출처</h2>
        <p>
          이 데모는 <strong>직접 만든 합성 데이터</strong>만 사용해요. 실제 은행 고객이나
          거래 데이터는 단 한 건도 없습니다. 합성인 대신, 데이터를 만드는 공식을{" "}
          <strong>실험 전에 고정</strong>하고 결과를 본 뒤 절대 고치지 않는 규칙으로
          정직성을 지켰어요. 자세한 한계는 &quot;한계와 다음 단계&quot; 탭에.
        </p>
      </section>
    </>
  );
}
